import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { login as apiLogin, refreshAccess } from '@/api/auth';
import { setAccessToken, setAuthFailureHandler } from '@/api/client';
import { decodeAccess, isAdsAdmin, isAdvertiser, isExpired, type AccessClaims } from '@/lib/jwt';
import { storage, type AuthMode } from '@/lib/storage';

interface AuthState {
  ready: boolean;
  authenticated: boolean;
  claims: AccessClaims | null;
  mode: AuthMode;
  isAdvertiser: boolean;
  isAdmin: boolean;
  login: (mode: AuthMode, username: string, password: string) => Promise<AccessClaims | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [claims, setClaims] = useState<AccessClaims | null>(null);
  const [mode, setMode] = useState<AuthMode>(storage.getMode());
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    storage.clearAuth();
    setClaims(null);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  const scheduleRefresh = useCallback(
    (c: AccessClaims | null, activeMode: AuthMode) => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      if (!c?.exp) return;
      const msUntil = c.exp * 1000 - Date.now() - 60_000; // refresh ~60s early
      const delay = Math.max(5_000, msUntil);
      refreshTimer.current = setTimeout(async () => {
        const token = storage.getRefresh();
        if (!token) return;
        try {
          const pair = await refreshAccess(activeMode, token);
          if (pair.access) {
            setAccessToken(pair.access);
            if (pair.refresh) storage.setRefresh(pair.refresh);
            const next = decodeAccess(pair.access);
            setClaims(next);
            scheduleRefresh(next, activeMode);
          }
        } catch {
          clearSession();
        }
      }, delay);
    },
    [clearSession],
  );

  // Bootstrap from a persisted refresh token.
  useEffect(() => {
    setAuthFailureHandler(() => {
      clearSession();
    });
    let cancelled = false;
    (async () => {
      const token = storage.getRefresh();
      const activeMode = storage.getMode();
      if (!token) {
        setReady(true);
        return;
      }
      try {
        const pair = await refreshAccess(activeMode, token);
        if (cancelled) return;
        if (pair.access) {
          setAccessToken(pair.access);
          if (pair.refresh) storage.setRefresh(pair.refresh);
          const c = decodeAccess(pair.access);
          setClaims(c);
          setMode(activeMode);
          scheduleRefresh(c, activeMode);
        }
      } catch {
        clearSession();
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
      setAuthFailureHandler(null);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [clearSession, scheduleRefresh]);

  const login = useCallback(
    async (loginMode: AuthMode, username: string, password: string) => {
      const pair = await apiLogin(loginMode, username, password);
      setAccessToken(pair.access);
      storage.setRefresh(pair.refresh);
      storage.setMode(loginMode);
      setMode(loginMode);
      const c = decodeAccess(pair.access);
      setClaims(c);
      scheduleRefresh(c, loginMode);
      return c;
    },
    [scheduleRefresh],
  );

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      authenticated: !!claims && !isExpired(claims, 0),
      claims,
      mode,
      isAdvertiser: isAdvertiser(claims),
      isAdmin: isAdsAdmin(claims),
      login,
      logout,
    }),
    [ready, claims, mode, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
